import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, from, lastValueFrom, map, Observable, of, shareReplay, startWith, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TagAverage } from 'src/app/common/stats-utils';
import { StatusWidgetMode } from 'src/app/pages/dashboard/widgets/status';
import { Totals } from 'src/app/pages/dashboard/widgets/totals';
import { PagedResult, PagingService } from 'src/app/services/paging/paging.service';
import { WorkSchedulesAttendanceIssue } from 'src/app/services/schedules';
import {
  AverageGroup,
  GetStatsOptions,
  ModeRatio,
  NewStatsComcatResult,
  NewStatsCompanyResult,
  NewStatsOptions,
  NewStatsUserResult,
  StatsTotal,
  Summary,
  SummaryRatio,
  WorkLifeBalanceResult
} from 'src/app/services/stats/stats.service';
import { CompanySettingsChanged, ExtCustomCompanySettings, UpdateCurrentUser, UserSettingsChanged } from 'src/app/store/auth/auth.actions';
import { AuthState } from 'src/app/store/auth/auth.state';
import { LoadManagedUsers, LoadUserGroups } from 'src/app/store/manager/manager.actions';
import { AuthCompany, DailySummary, DemoDashboardId, HighestRatioMode, MeetingLog, Screencast, Timeuse, TimeuseEntity, TimeuseScore, User, Worklog, WorkRange } from 'src/models';
import { CompanyService } from '../company/company.service';
import { GrowthBookService } from '../growthbook/service';
import { ExperimentProps } from '../growthbook/types';
import {
    CalendarSummaryResponse,
    GoogleCalendarMeetingDataItem,
    GoogleCalendarUsersDataItem,
    IdleTimeData,
    MeetingMetricsData,
    TotalMeetingTimeData,
    UserMeetingDetails,
    WebAppUsageData
} from '../meeting-insights/meeting-insights.service';
import { SegmentService } from '../segment/segment.service';
import { UserService } from '../user/user.service';
import { getDemoCalendarMeetingStart, getDemoDateBuckets, withBucketDateOptions } from './demo-date-range.mock';
import {
  evaluateIsDashboardMockDataActive,
  getDemoDashboardIdFromRoute,
  hasTrackedProjectId,
  isMockDataExperimentVariantB,
  SHOW_MOCK_DATA_EXPERIMENT_KEY
} from './demo-mock-data-visibility';
import { buildDemoDashboardTaskTotals } from './demo-projects-report.mock';
import { buildDemoReportLastTrack, buildDemoReportUsers, filterDemoReportUsers, getDemoTeamName } from './demo-report-users.mock';
import { DemoSeedService } from './demo-seed.service';
import { User as MockStoreUser } from './mock-data-store';
import { MockStatsService, resolveMockUserIds } from './mock-stats.service';
import { generateMockDailySummariesForRange, generateMockMeetingLogsForRange, generateMockWorklogsForRange } from './mock-timeline-worklogs';

/** Canonical mock user id for automatic/silent tracking on the individual dashboard. */
export const DEMO_SILENT_USER_ID = 'user-silent-1' as const;

interface ActiveUser extends User {
  lastActiveTrack: boolean;
}

interface ProjectDistribution {
  id: string;
  name: string;
  percentage: number;
}

interface DemoDataProps {
  [key: string]: string;
}

@Injectable({
  providedIn: 'root',
})
export class DemoService implements OnDestroy {
  // Add a BehaviorSubject to track the demo data state
  private readonly showDemoDataSubject = new BehaviorSubject<boolean | undefined>(undefined);
  public showDemoData$ = this.showDemoDataSubject.asObservable();

  /** Mock data visibility for the executive dashboard, independent of the current URL. */
  public readonly showExecutiveDemoData$: Observable<boolean | undefined>;

  /** Mock data visibility for the Benchmarks AI report, independent of the current URL. */
  public readonly showBenchmarksDemoData$: Observable<boolean | undefined>;

  /**
   * Sample-data visibility for a specific route. Includes the GrowthBook experiment gate,
   * so callers do not have to read `companySettings.custom.showDemoData` on their own.
   */
  showDemoDataOnRoute(route: string): Observable<boolean | undefined> {
    return this.buildShowDemoDataStream(route);
  }

  /**
   * Synchronous snapshot of the latest {@link showDemoData$} value. Returns `false` when the
   * subject has not yet emitted a definitive boolean. Used by services that need to gate
   * synchronous data sources (e.g. {@link BenchmarkingService.fetchPaginatedMetrics}).
   */
  public isShowDemoDataActiveSnapshot(): boolean {
    return this.showDemoDataSubject.value === true;
  }

  // User data
  private readonly userIds: string[];
  private readonly userNames: string[];
  private readonly userCount: number;
  private activeUsers: ActiveUser[] | null = null; // Properly typed and nullable

  // Project distribution (must sum to 1.0)
  private readonly projectDistribution: ProjectDistribution[] = [
    { id: 'Z_xiqjD8_Rus64aR', name: 'General Work', percentage: 0.15 },
    { id: 'Y_lmn45P_Qrt78bS', name: 'Marketing Campaign', percentage: 0.25 },
    { id: 'X_opq23R_Pst91cT', name: 'UI/UX Redesign', percentage: 0.10 },
    { id: 'W_uvw67S_Ouv04dU', name: 'Backend Migration', percentage: 0.30 },
    { id: 'V_xyz89T_Nwx17eV', name: 'Customer Onboarding', percentage: 0.20 },
  ];

  // Fix: Make workLifeBalanceGroups$ private and add proper management
  private workLifeBalanceGroups$: Observable<WorkLifeBalanceResult[]> | null = null;
  private workLifeBalanceGroupsSubscription: Subscription | null = null;
  private lastWorkLifeBalanceParams: any = null;

  // Add destroy subject for proper cleanup
  private readonly destroy$ = new Subject<void>();

  // Add timeout IDs for cleanup
  private memoryCleanupTimeoutId: NodeJS.Timeout | null = null;

  // Track all active subscriptions for proper cleanup
  private readonly activeSubscriptions = new Set<Subscription>();

  /** Avoid overlapping PUTs while clearing legacy `custom.showDemoData` after tracked time is detected. */
  private syncingLegacyShowDemoDataOff = false;

  /** True once {@link showDemoData$} has emitted active mock data in this session. */
  private mockDataWasActiveBeforeTrackedTime = false;

  /** Ensures {@link trackMockDataDismissedByTrackedTimeIfNeeded} fires at most once per session. */
  private trackedTimeDismissEventFired = false;

  /**
   * Once true, mock data stays off for the session due to tracked time — stop combining
   * {@link ManagerState.users} (avoids O(n) rescan on every live status patch).
   */
  private readonly sessionBlockedByTrackedTime$ = new BehaviorSubject<boolean>(false);

  /** Dispatches {@link LoadManagedUsers} at most once, and only while mock data is still eligible. */
  private managedUsersLoadRequested = false;

  constructor(
    private readonly growthBook: GrowthBookService,
    private readonly store: Store,
    private readonly companyService: CompanyService,
    private readonly userService: UserService,
    private readonly mockStats: MockStatsService,
    private readonly demoSeed: DemoSeedService,
    private readonly router: Router,
    private readonly paging: PagingService,
    private readonly segment: SegmentService,
  ) {
    // Initialize user data from mockStats
    const mockUsers = this.mockStats.mockUsers;
    this.userIds = mockUsers.map(user => user.id);
    this.userNames = mockUsers.map(user => user.name);

    // Set userCount based on userIds length
    this.userCount = this.userIds.length;

    // Initialize the BehaviorSubject value when service is created
    this.initShowDemoData();
    this.showExecutiveDemoData$ = this.buildShowDemoDataStream('/dashboard-executive').pipe(
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.showBenchmarksDemoData$ = this.buildShowDemoDataStream('/benchmarks').pipe(
      distinctUntilChanged(),
      shareReplay(1),
    );

    // Initialize baseSeconds in mockStats service instead
    this.mockStats.initBaseSeconds();

    // Initialize userBaseSeconds in mockStats service
    this.mockStats.initUserBaseSeconds();

    // Start periodic memory cleanup
    this.performMemoryCleanup();
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to unsubscribe
    this.destroy$.next();
    this.destroy$.complete();

    // Clear cached data to prevent memory leaks
    this.activeUsers = null;
    this.workLifeBalanceGroups$ = null;

    // Clear timeout IDs for cleanup
    if (this.memoryCleanupTimeoutId) {
      clearTimeout(this.memoryCleanupTimeoutId);
      this.memoryCleanupTimeoutId = null;
    }

    // Complete the BehaviorSubject to prevent memory leaks
    this.showDemoDataSubject.complete();
    this.sessionBlockedByTrackedTime$.complete();

    // Clean up all active subscriptions
    this.activeSubscriptions.forEach(subscription => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
    this.activeSubscriptions.clear();

    // Clean up workLifeBalanceGroups subscription
    this.cleanupWorkLifeBalanceGroups();
  }

  // Private method to initialize the BehaviorSubject with proper subscription management
  private initShowDemoData(): void {
    const subscription = this.showDemoData().pipe(
      distinctUntilChanged(),
      catchError(error => {
        Sentry.captureException(error, {
          tags: {
            component: 'DemoService',
            action: 'initShowDemoData',
            context: 'initialization',
          },
        });
        return of(false);
      }),
      takeUntil(this.destroy$),
    ).subscribe(value => {
      const previous = this.showDemoDataSubject.value;
      this.showDemoDataSubject.next(value);
      // Groups loaded while mock data was on stay in ManagerState; refetch live tags after dismiss.
      if (previous === true && value === false) {
        this.store.dispatch(new LoadUserGroups());
      }
    });

    // Track the subscription for cleanup
    this.activeSubscriptions.add(subscription);
  }

  public getWorkRange(params: any): PagedResult<WorkRange> {
    return this.mockStats.getWorkRange(params);
  }

  public getDemoWorklogSliceByDays(
    fromIso: string,
    toIso: string,
    users: string[],
    params?: { limit?: number, mode?: string, [key: string]: any },
  ): PagedResult<Worklog[]> {
    const realLimit = params?.limit || 7;
    const fromDt = DateTime.fromISO(fromIso);
    const toDt = DateTime.fromISO(toIso);
    const totalDays = toDt.diff(fromDt, 'days').days;
    const totalPages = Math.ceil(totalDays / realLimit) || 1;
    const userId = users[0] || '';

    return this.paging.getPagedResult<Worklog[]>((page) => {
      const localTo = toDt.minus({ days: page * 7 });
      const localFrom = DateTime.max(fromDt, localTo.minus({ days: realLimit }));
      const mockLogs = generateMockWorklogsForRange(
        localFrom.toUTC().toISO(),
        localTo.toUTC().toISO(),
        userId,
        this.demoSeed,
        params?.timezone,
      );
      return of({ data: [mockLogs] });
    }, 1, totalPages);
  }

  /** Full-range worklogs for the standalone Timeline report (one user). */
  public getDemoWorklogsForUser(fromIso: string, toIso: string, userId: string, timezone?: string): Worklog[] {
    return generateMockWorklogsForRange(fromIso, toIso, userId, this.demoSeed, timezone);
  }

  /** Meeting ranges merged into the standalone Timeline report (one user). */
  public getDemoMeetingLogsForUser(fromIso: string, toIso: string, userId: string, timezone?: string): MeetingLog[] {
    return generateMockMeetingLogsForRange(fromIso, toIso, userId, this.demoSeed, timezone);
  }

  /** Weekly timeline rows derived from the same seeded worklogs as the daily view. */
  public getDemoDailySummariesForUser(
    fromIso: string,
    toIso: string,
    userId: string,
    timezone?: string,
  ): DailySummary[] {
    return generateMockDailySummariesForRange(fromIso, toIso, userId, this.demoSeed, timezone);
  }

  public newStats<T>(options: NewStatsOptions, mode?: any): PagedResult<T> {
    return this.mockStats.newStats(options, mode);
  }

  public newStatsByUsers<T>(options: NewStatsOptions): PagedResult<T> {
    return this.mockStats.newStatsByUsers(options);
  }

  public newStatsTotal<T>(options: NewStatsOptions): PagedResult<T> {
    return this.mockStats.newStatsTotal(options);
  }

  public newStatsTimesheet<T>(options: NewStatsOptions): PagedResult<T> {
    return this.mockStats.newStatsTimesheet(options);
  }

  public newTimesheetTotal<T>(options: NewStatsOptions): PagedResult<T> {
    return this.mockStats.newTimesheetTotal(options);
  }

  public getProjectsTotal(options?: GetStatsOptions): PagedResult<StatsTotal> {
    return this.mockStats.getProjectsTotal(options);
  }

  public getTasksTotal(options?: GetStatsOptions): PagedResult<StatsTotal> {
    return this.mockStats.getTasksTotal(options);
  }

  public getTimesheetTasks(options?: GetStatsOptions): PagedResult<StatsTotal> {
    return this.mockStats.getTimesheetTasks(options);
  }

  public getScreencasts(params: any): PagedResult<Screencast> {
    return this.mockStats.getScreencasts(params);
  }

  public getWorkingUsers(mode: StatusWidgetMode): Observable<ActiveUser[]> {
    // Initialize activeUsers only once and cache the result
    if (!this.activeUsers) {
      this.activeUsers = this.mockStats.mockUsers.map(user => {
        // Create a copy of the user to avoid modifying the original
        const userCopy = { ...user, lastActiveTrack: this.demoSeed.float('active-users', user.id) > 0.4 } as unknown as ActiveUser;
        return userCopy;
      });
    }

    if (mode === 'currently-working') {
      return of(this.activeUsers.filter(user => user.lastActiveTrack));
    }

    return of(this.activeUsers.filter(user => !user.lastActiveTrack));
  }

  public getTotal(apiParams?: any): PagedResult<Summary> {
    return this.mockStats.getTotal(apiParams);
  }

  public getTimesheetModeRatioDaily(options?: GetStatsOptions): PagedResult<ModeRatio[]> {
    return this.mockStats.getTimesheetModeRatioDaily(options);
  }

  public getDemoTotals(apiParams?: any): Totals {
    return this.mockStats.getDemoTotals(apiParams);
  }

  public getAverageWorkedGroups(parameters: any): Observable<AverageGroup[]> {
    if (!parameters) {
      return of(null as unknown as AverageGroup[]);
    }
    return from(this.mockStats.getAverageWorkedGroups(parameters).page(0)).pipe(
      startWith(null as unknown as AverageGroup[]),
      catchError(() => of([] as AverageGroup[])),
      takeUntil(this.destroy$),
    );
  }

  public getAttendanceIssues(parameters: any): Observable<WorkSchedulesAttendanceIssue[]> {
    if (!parameters) {
      return of(null as unknown as WorkSchedulesAttendanceIssue[]);
    }
    return from(this.mockStats.getAttendanceIssues(parameters).page(0)).pipe(
      startWith(null as unknown as WorkSchedulesAttendanceIssue[]),
      catchError(() => of([] as WorkSchedulesAttendanceIssue[])),
      takeUntil(this.destroy$),
    );
  }

  private demoTagAverage(id: string, name: string, avg: number): TagAverage {
    return { tagId: id, tagName: name, avg };
  }

  private demoTagRatio(id: string, name: string, ratio: number): TagAverage {
    return { tagId: id, tagName: name, ratio };
  }

  /** Totals shape for executive overview preset (matches TotalsOverviewPresetService.convertData output). */
  public getDemoExecutiveOverviewTotals(apiParams: any): Totals {
    const base = this.mockStats.getDemoTotals(apiParams);
    const count = Math.max(1, base.count ?? this.userCount);
    const minTeamName = getDemoTeamName(4);
    const maxTeamName = getDemoTeamName(0);
    const maxUnprodTeamName = getDemoTeamName(1);
    const minTagAvg = this.demoTagAverage('demo-min-avg', minTeamName, 20000);
    const maxTagAvg = this.demoTagAverage('demo-max-avg', maxTeamName, 35000);
    const minTagAvgPerDay = this.demoTagAverage('demo-min-day', minTeamName, 4000);
    const maxTagAvgPerDay = this.demoTagAverage('demo-max-day', maxTeamName, 6200);
    const minTagUnprodRatio = this.demoTagRatio('demo-min-u', minTeamName, 0.08);
    const maxTagUnprodRatio = this.demoTagRatio('demo-max-u', maxUnprodTeamName, 0.24);
    const total = base.total ?? 0;
    return {
      avgPerUser: Math.round(total / count),
      avgPerUserPerDay: Math.round(total / count / 7),
      unprodRatio: base.unprodRatio ?? 0,
      newUsers: base.newUsersTotal ?? 2,
      usersWhoTrackedTime: count,
      subStats: {
        minTagAvg: minTagAvg.avg,
        minTagAvgName: minTagAvg.tagName,
        maxTagAvg: maxTagAvg.avg,
        maxTagAvgName: maxTagAvg.tagName,
        minTagAvgPerDay: minTagAvgPerDay.avg,
        minTagAvgPerDayName: minTagAvgPerDay.tagName,
        maxTagAvgPerDay: maxTagAvgPerDay.avg,
        maxTagAvgPerDayName: maxTagAvgPerDay.tagName,
        minTagUnprodRatio: minTagUnprodRatio.ratio,
        minTagUnprodRatioName: minTagAvg.tagName,
        maxTagUnprodRatio: maxTagUnprodRatio.ratio,
        maxTagUnprodRatioName: maxTagUnprodRatio.tagName,
      },
    };
  }

  public getDemoExecutiveOutliersTotals(): Totals {
    return {
      tooMuchUnprodTime: 3,
      tooManyWorkLifeIssues: 2,
      tooManyWorkHours: 4,
      tooMuchUnprodTimeUsers: [{ userId: 'demo-u1', name: 'Demo User' }] as unknown as Totals['tooMuchUnprodTimeUsers'],
      tooManyWorkLifeIssuesUsers: [{ userId: 'demo-u2', name: 'Demo User 2' }] as unknown as Totals['tooManyWorkLifeIssuesUsers'],
      tooManyWorkHoursUsers: [{ userId: 'demo-u3', name: 'Demo User 3' }] as unknown as Totals['tooManyWorkHoursUsers'],
    };
  }

  public getDemoExecutiveCompanyConfigurationTotals(): Totals {
    return {
      adminsCount: 2,
      managersCount: 5,
      regularUsersCount: 18,
      usersWithScreenshotsEnabled: 16,
      usersWithBlurScreenshotsEnabled: 4,
      usersWithEditTimeEnabled: 10,
      totalUsers: 25,
    };
  }

  public getDemoProjectTotals(apiParams?: any): Observable<any> {
    // Get the total seconds from getDemoTotals to ensure consistency
    const totals = this.mockStats.getDemoTotals(apiParams);
    const totalSeconds = totals.total ?? 0;

    // Calculate project totals based on percentages of the total time
    const projects = this.projectDistribution.map(project => ({
      id: project.id,
      total: Math.round(totalSeconds * project.percentage),
      name: project.name,
    }));

    // Return as Observable with the simplified format
    return of(projects);
  }

  public getDemoTaskTotals(apiParams?: GetStatsOptions): Observable<StatsTotal[]> {
    const totals = this.mockStats.getDemoTotals(apiParams);
    const limit = apiParams?.limit ?? 5;
    return of(buildDemoDashboardTaskTotals(totals.total ?? 0, limit));
  }

  public getSummaryRatio(options?: GetStatsOptions) {
    return this.mockStats.getSummaryRatio(options);
  }

  public getModeRatio(apiParams?: GetStatsOptions, mode?: 'most' | 'least')  {
    return this.mockStats.getModeRatio(apiParams, mode);
  }

  public getDemoModeRatio(apiParams?: any): Observable<any> {
    // Get the total seconds from getDemoTotals to ensure consistency
    const totals = this.mockStats.getDemoTotals(apiParams);
    const totalSeconds = totals.total ?? 0;
    const standardRatios = this.mockStats.standardRatios;

    // Calculate time distribution based on standard ratios
    const computerSeconds = Math.round(totalSeconds * standardRatios.computer);
    const mobileSeconds = Math.round(totalSeconds * standardRatios.mobile);
    const manualSeconds = Math.round(totalSeconds * standardRatios.manual);
    const offcomputerSeconds = Math.round(totalSeconds * standardRatios.offcomputer);
    const meetingSeconds = Math.round(totalSeconds * standardRatios.meeting);
    const paidBreakSeconds = Math.round(totalSeconds * standardRatios.paidBreak);

    // Create the ModeRatio object with the new format
    const modeRatio = {
      total: totalSeconds,
      offcomputer: offcomputerSeconds,
      computer: computerSeconds,
      mobile: mobileSeconds,
      manual: manualSeconds,
      ratio: computerSeconds / totalSeconds,
      paidBreak: paidBreakSeconds,
      mobMan: mobileSeconds + manualSeconds, // Combined mobile and manual time
      userId: this.userIds,
      meeting: meetingSeconds,
      modeTotal: totalSeconds,
      computerRatio: standardRatios.computer,
      manualRatio: standardRatios.manual,
      mobileRatio: standardRatios.mobile,
      offcomputerRatio: standardRatios.offcomputer,
      meetingRatio: standardRatios.meeting,
      count: this.userCount,
      partial: 0,
    };

    // Return as Observable with the data in the expected format
    return of(modeRatio);
  }

  public getDemoTimesheetSummaryRatio(options?: GetStatsOptions) {
    // Get the base totals to ensure consistency with other demo methods
    const totals = this.mockStats.getDemoTotals(options);
    const standardRatios = this.mockStats.standardRatios;

    // Get date range from options
    const fromDate = options?.from ? DateTime.fromISO(options.from) : DateTime.now().minus({ days: 7 });
    const toDate = options?.to ? DateTime.fromISO(options.to) : DateTime.now();
    const days = Math.ceil(toDate.diff(fromDate, 'days').days);

    // Use totals.total directly as it's already in seconds
    const totalSeconds = totals.total ?? 0;

    // Distribute total across days
    const secondsPerDay = Math.round(totalSeconds / Math.max(1, days));

    // Generate data for each day in the range
    const data: SummaryRatio[][] = [];
    for (let i = 0; i < days; i++) {
      // Add slight variation per day but ensure the overall total remains consistent
      const dayVariationFactor = this.randomBetween(0.9, 1.1, 'timesheet-summary-ratio', 'day', i);
      const dayTotalSeconds = Math.round(secondsPerDay * dayVariationFactor);

      // Distribute day total among users
      const userTotals = this.distributeTotal(dayTotalSeconds, this.userCount);

      // Generate data for each user
      const dayData: SummaryRatio[] = this.userIds.map((userId, index) => {
        const userTotal = userTotals[index];

        const variationFactor = this.randomBetween(0.9, 1.1, 'timesheet-summary-ratio', 'day', i, 'user', userId, 'variation');

        const idleSec = Math.floor(userTotal * standardRatios.idle * variationFactor);
        const idleMins = Math.floor(idleSec / 60);
        const prodRatio = standardRatios.productive * this.randomBetween(0.95, 1.05, 'timesheet-summary-ratio', 'day', i, 'user', userId, 'prodRatio');
        const prod = Math.floor(userTotal * prodRatio);
        const unprod = Math.floor(userTotal * standardRatios.unproductive * variationFactor);
        const manual = Math.floor(userTotal * standardRatios.manual * variationFactor);
        const mobile = Math.floor(userTotal * standardRatios.mobile * variationFactor);
        const meeting = Math.floor(userTotal * standardRatios.meeting * variationFactor);

        return {
          userId,
          total: userTotal,
          idleSec,
          idleMins,
          idleSecRatio: idleSec / userTotal,
          idleMinsRatio: idleMins / (userTotal / 60),
          prod,
          prodRatio: prod / userTotal,
          unprod,
          unprodRatio: unprod / userTotal,
          manual,
          manualRatio: manual / userTotal,
          mobile,
          mobileRatio: mobile / userTotal,
          keysPerNonIdleMin: Math.floor(this.randomBetween(80, 150, 'timesheet-summary-ratio', 'day', i, 'user', userId, 'keys')),
          modeTotal: userTotal,
          scoreTotal: userTotal,
          activityLevel: this.randomBetween(0.6, 0.95, 'timesheet-summary-ratio', 'day', i, 'user', userId, 'activity'),
          unrated: Math.floor(userTotal * this.randomBetween(0.02, 0.08, 'timesheet-summary-ratio', 'day', i, 'user', userId, 'unrated')),
          meeting,
          meetingRatio: meeting / userTotal,
          count: 1,
        };
      });

      data.push(dayData);
    }

    // Return paged result
    return of({
      data,
      loading: false,
      page: 0,
      pageSize: options?.limit || days,
      totalCount: days,
      hasMore: false,
      loadMore: () => {},
      refresh: () => {},
      raw: () => Promise.resolve({
        data,
        paging: {
          page: 0,
          totalCount: days,
          cur: 0,
          limit: options?.limit || days,
        },
      }),
    });
  }

  public getDemoTimesheetModeRatio(options?: GetStatsOptions): Observable<any> {
    // Get the base totals to ensure consistency with other demo methods
    const totals = this.mockStats.getDemoTotals(options);

    // Generate mock data for each day in the range
    const fromDate = options?.from ? DateTime.fromISO(options.from) : DateTime.now().minus({ days: 7 });
    const toDate = options?.to ? DateTime.fromISO(options.to) : DateTime.now();
    const days = Math.ceil(toDate.diff(fromDate, 'days').days);
    const limit = options?.limit || days;

    // Get user IDs from options or use the class property
    const userIds = Array.isArray(options?.user)
      ? options.user
      : this.userIds;

    // Calculate average seconds per day based on totals
    const totalSeconds = totals.total ?? 0;
    const avgSecondsPerDay = Math.round(totalSeconds / Math.max(1, days));

    // Generate mock data for each day with aggregated user data
    const mockData = Array(Math.min(days, limit)).fill(0).map((_, dayIdx) => {
      const variationFactor = this.randomBetween(0.9, 1.1, 'timesheet-mode-ratio', 'day', dayIdx);
      const total = Math.round(avgSecondsPerDay * variationFactor);

      const computerRatio = this.mockStats.standardRatios.computer * this.randomBetween(0.95, 1.05, 'timesheet-mode-ratio', 'day', dayIdx, 'computer');
      const mobileRatio = this.mockStats.standardRatios.mobile * this.randomBetween(0.95, 1.05, 'timesheet-mode-ratio', 'day', dayIdx, 'mobile');
      const manualRatio = this.mockStats.standardRatios.manual * this.randomBetween(0.95, 1.05, 'timesheet-mode-ratio', 'day', dayIdx, 'manual');
      const meetingRatio = this.mockStats.standardRatios.meeting * this.randomBetween(0.95, 1.05, 'timesheet-mode-ratio', 'day', dayIdx, 'meeting');
      const paidBreakRatio = this.mockStats.standardRatios.paidBreak * this.randomBetween(0.95, 1.05, 'timesheet-mode-ratio', 'day', dayIdx, 'paidBreak');

      const computer = Math.floor(total * computerRatio);
      const mobile = Math.floor(total * mobileRatio);
      const manual = Math.floor(total * manualRatio);
      const meeting = Math.floor(total * meetingRatio);
      const paidBreak = Math.floor(total * paidBreakRatio);
      const offcomputer = total - computer - mobile - manual;

      // Return a single aggregated object per day with userIds as an array
      return [{
        userId: userIds, // Array of user IDs
        total,
        computer,
        mobile,
        manual,
        offcomputer,
        meeting,
        paidBreak,
        ratio: computer / total,
        computerRatio: computer / total,
        mobileRatio: mobile / total,
        manualRatio: manual / total,
        offcomputerRatio: offcomputer / total,
        paidBreakRatio: paidBreak / total,
        meetingRatio: meeting / total,
        modeTotal: total,
        partial: 0,
        count: userIds.length,
      }];
    });

    return of(mockData);
  }

  public showDemoData(): Observable<boolean | undefined> {
    return this.buildShowDemoDataStream();
  }

  private buildShowDemoDataStream(fixedRoute?: string): Observable<boolean | undefined> {
    try {
      // Re-read experiment after GrowthBook finishes loading (constructor may run before features load).
      const experimentAllowed$ = this.growthBook.getFeature<ExperimentProps>(
        SHOW_MOCK_DATA_EXPERIMENT_KEY,
      ).pipe(
        map(experiment => isMockDataExperimentVariantB(experiment)),
        distinctUntilChanged(),
      );

      // Get company data reactively from store
      const company$ = this.store.select(AuthState.company).pipe(
        map(company => ({
          demoDataHiddenDashboards: company?.userSettings?.custom?.['demoDataHiddenDashboards'],
          showDemoData: company?.companySettings?.custom?.['showDemoData'] as boolean | undefined,
          companyLastTrackProjectId: company?.lastTrack?.projectId,
          trackingMode: company?.companySettings?.trackingMode,
        })),
        catchError(error => {
          Sentry.captureException(error, {
            tags: {
              component: 'DemoService',
              action: 'showDemoData',
              context: 'company_selector',
            },
          });
          return of({
            demoDataHiddenDashboards: undefined,
            showDemoData: undefined,
            companyLastTrackProjectId: undefined,
            trackingMode: undefined,
          });
        }),
      );

      // Select manager.users without importing ManagerState — that import creates a
      // circular module init: DemoService → ManagerState → DemoManageUsersStore → DemoService.
      const users$ = this.store.select((state: { manager?: { users?: User[] } }) =>
        (state.manager?.users ?? []).filter(user => !user.archived),
      ).pipe(
        catchError(error => {
          Sentry.captureException(error, {
            tags: {
              component: 'DemoService',
              action: 'showDemoData',
              context: 'users_selector',
            },
          });
          return of([]);
        }),
      );

      const currentRoute$ = fixedRoute != null
        ? of(fixedRoute)
        : this.router.events.pipe(
          map(() => this.router.url),
          startWith(this.router.url),
          distinctUntilChanged(),
          catchError(error => {
            Sentry.captureException(error, {
              tags: {
                component: 'DemoService',
                action: 'showDemoData',
                context: 'route_events',
              },
            });
            return of('/');
          }),
        );

      return experimentAllowed$.pipe(
        switchMap(experimentAllowed => {
          if (!experimentAllowed) {
            return of(false);
          }

          return combineLatest([company$, currentRoute$, this.sessionBlockedByTrackedTime$]).pipe(
            switchMap(([companyData, currentRoute, sessionBlocked]) => {
              // Tracked-time accounts: session latch / company lastTrack / legacy flag — no user list needed.
              if (sessionBlocked) {
                return of({ isActive: false, blockedByTrackedTime: true });
              }

              const companyBlocks = hasTrackedProjectId(companyData.companyLastTrackProjectId);
              const showDemoEligible = companyData.showDemoData === true;

              if (companyBlocks || !showDemoEligible) {
                return of(evaluateIsDashboardMockDataActive({
                  users: [],
                  companyLastTrackProjectId: companyData.companyLastTrackProjectId,
                  demoDataHiddenDashboards: companyData.demoDataHiddenDashboards,
                  showDemoData: companyData.showDemoData,
                  trackingMode: companyData.trackingMode,
                  currentRoute,
                }));
              }

              // Still eligible for mock: load managed users once (other guards/pages may already have them).
              this.requestManagedUsersIfNeeded();

              return users$.pipe(
                map(users =>
                  evaluateIsDashboardMockDataActive({
                    users,
                    companyLastTrackProjectId: companyData.companyLastTrackProjectId,
                    demoDataHiddenDashboards: companyData.demoDataHiddenDashboards,
                    showDemoData: companyData.showDemoData,
                    trackingMode: companyData.trackingMode,
                    currentRoute,
                  }),
                ),
              );
            }),
            tap(({ isActive, blockedByTrackedTime }) => {
              if (blockedByTrackedTime) {
                // Latch so subsequent status patches no longer resubscribe to manager.users.
                if (!this.sessionBlockedByTrackedTime$.value) {
                  this.sessionBlockedByTrackedTime$.next(true);
                }
                this.trackMockDataDismissedByTrackedTimeIfNeeded();
                this.ensureShowDemoDataSettingFalseWhenTracked();
              } else if (isActive) {
                this.mockDataWasActiveBeforeTrackedTime = true;
              }
            }),
            map(({ isActive }) => isActive),
          );
        }),
        catchError(error => {
          Sentry.captureException(error, {
            tags: {
              component: 'DemoService',
              action: 'showDemoData',
              context: 'combine_latest',
            },
          });
          return of(false);
        }),
        // Ensure proper cleanup to prevent memory leaks
        takeUntil(this.destroy$),
      );
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'DemoService',
          action: 'showDemoData',
          context: 'method_entry',
        },
      });
      return of(false);
    }
  }

  /**
   * Loads managed users only when mock data is still eligible (experiment B + showDemoData +
   * no company lastTrack). Tracked-time accounts skip this entirely.
   */
  private requestManagedUsersIfNeeded(): void {
    if (this.managedUsersLoadRequested) {
      return;
    }
    this.managedUsersLoadRequested = true;
    this.store.dispatch(new LoadManagedUsers(true));
  }

  /**
   * Maps the current URL to a dashboard id for per-dashboard sample-data visibility.
   * @see {@link getDemoDashboardIdFromRoute} — implementation lives in `demo-mock-data-visibility.ts` for reuse/tests.
   */
  static getDemoDashboardIdFromRoute(route: string): DemoDashboardId | undefined {
    return getDemoDashboardIdFromRoute(route);
  }

  getDemoMeetingMetricsData(parameters: { userApiParams?: any }): Observable<MeetingMetricsData> {
    const totals = this.mockStats.getDemoTotals(parameters?.userApiParams);
    const meetingSeconds = Math.round((totals.total ?? 0) * this.mockStats.standardRatios.meeting);
    // Empty user/group selection: skip mock floors so count/idle KPIs stay at 0.
    if (meetingSeconds <= 0) {
      return of({
        meetingTime: 0,
        meetingCount: 0,
        avgMeetingDuration: 0,
        avgIdleTimePerMeeting: 0,
      });
    }
    const meetingCount = Math.max(3, Math.round(meetingSeconds / 7200));
    const avgMeetingDuration = Math.floor(meetingSeconds / meetingCount);
    const avgIdleTimePerMeeting = 180 + Math.floor((meetingSeconds % 7) * 12);
    return of({
      meetingTime: meetingSeconds,
      meetingCount,
      avgMeetingDuration,
      avgIdleTimePerMeeting,
    });
  }

  getDemoWebAppUsageDuringMeetings(apiParams?: { user?: string | string[], tag?: string | string[] }): Observable<WebAppUsageData[]> {
    const scale = this.demoMeetingFilterScale(apiParams);
    const bias = this.demoMeetingFilterBias(apiParams);
    return of([
      { name: 'Zoom', meetingTotal: Math.round((5400 - bias * 200) * scale) },
      { name: 'Microsoft Teams', meetingTotal: Math.round((4200 + bias * 150) * scale) },
      { name: 'Google Meet', meetingTotal: Math.round((3100 + bias * 80) * scale) },
      { name: 'Slack', meetingTotal: Math.round((1800 - bias * 50) * scale) },
    ]);
  }

  getDemoIdleTimeMeetingInsights(apiParams?: { user?: string | string[], tag?: string | string[] }): Observable<IdleTimeData[]> {
    const scale = this.demoMeetingFilterScale(apiParams);
    const bias = this.demoMeetingFilterBias(apiParams);
    const total = Math.round(28800 * scale);
    const meeting = Math.round(7200 * scale);
    const idleMinsMeeting = Math.round(45 * scale);
    const idleOutsideMeetingMins = Math.round(120 * scale);
    return of([{
      meeting,
      meetingRatio: total > 0 ? meeting / total : 0,
      idleMinsMeeting,
      idleOutsideMeetingMins,
      idleMinsDuringMeetingRatio: 0.18 + (bias % 7) * 0.01,
      idleMinsOutsideMeetingRatio: 0.12 + (bias % 5) * 0.01,
      total,
    }]);
  }

  getDemoTotalMeetingTimeRanking(
    mode: 'most' | 'least',
    userMode: 'group' | 'user',
    apiParams?: { user?: string | string[], tag?: string | string[] },
  ): Observable<TotalMeetingTimeData[]> {
    const allUsers = this.mockStats.mockUsers || [];
    const users = this.filterMockUsersByApiParams(apiParams);

    let rows: TotalMeetingTimeData[];

    if (userMode === 'group') {
      const byTag = new Map<string, { tagId: string, tagName: string, userIds: string[], total: number, meeting: number }>();
      users.forEach((u) => {
        const i = Math.max(allUsers.findIndex(user => user.id === u.id), 0);
        const userTotal = 20000 + i * 1800;
        const meetingRatio = mode === 'most'
          ? 0.35 - i * 0.02 + 0.01 * (i % 3)
          : 0.08 + i * 0.015 + 0.01 * (i % 2);
        const userMeeting = Math.floor(userTotal * Math.min(0.9, Math.max(0.05, meetingRatio)));
        const g = byTag.get(u.tagId) ?? { tagId: u.tagId, tagName: u.tagName, userIds: [], total: 0, meeting: 0 };
        g.userIds.push(u.id);
        g.total += userTotal;
        g.meeting += userMeeting;
        byTag.set(u.tagId, g);
      });
      rows = Array.from(byTag.values()).map(g => ({
        meeting: g.meeting,
        meetingRatio: g.meeting / g.total,
        total: g.total,
        modeTotal: g.total,
        name: g.tagName,
        userName: g.tagName,
        currentTagName: g.tagName,
        currentTagId: g.tagId,
        userId: g.userIds.join(','),
        id: g.tagId,
      }));
    } else {
      rows = users.slice(0, 8).map((u) => {
        const i = Math.max(allUsers.findIndex(user => user.id === u.id), 0);
        const total = 14000 + i * 1200;
        const meetingRatio = mode === 'most'
          ? 0.30 - i * 0.025 + 0.01 * (i % 3)
          : 0.06 + i * 0.018 + 0.01 * (i % 2);
        const meeting = Math.floor(total * Math.min(0.9, Math.max(0.05, meetingRatio)));
        return {
          meeting,
          meetingRatio: meeting / total,
          total,
          modeTotal: total,
          name: u.name,
          userName: u.name,
          currentTagName: '',
          currentTagId: '',
          userId: u.id,
          id: u.id,
        };
      });
    }

    const sorted = [...rows].sort((a, b) =>
      mode === 'most' ? b.meetingRatio - a.meetingRatio : a.meetingRatio - b.meetingRatio,
    );
    return of(sorted.slice(0, 5));
  }

  private demoGcAttendeeStats(external: boolean): GoogleCalendarMeetingDataItem['attendeeStats'] {
    return {
      total: 6,
      accepted: 4,
      declined: 0,
      tentative: 1,
      needsAction: 1,
      internal: external ? 4 : 6,
      external: external ? 2 : 0,
    };
  }

  getDemoGoogleCalendarMeetingsPaged(options: any = {}): PagedResult<GoogleCalendarMeetingDataItem> {
    const n = 5;
    const items: GoogleCalendarMeetingDataItem[] = [];
    for (let i = 0; i < n; i++) {
      const start = getDemoCalendarMeetingStart(options, i, n);
      const end = start.plus({ hours: 1 });
      const isoStart = start.toISO();
      const isoEnd = end.toISO();
      const external = i % 2 === 0;
      items.push({
        _id: `demo-cal-meeting-${i}`,
        company: 'demo',
        eventId: `demo-event-${i}`,
        user: this.userIds[0],
        attendeeStats: this.demoGcAttendeeStats(external),
        created: isoStart,
        createdAt: isoStart,
        creator: { email: 'organizer@example.com' },
        durationMinutes: 60,
        end: isoEnd,
        hasAttendees: true,
        isAllDay: false,
        isRecurring: false,
        lastSynced: isoStart,
        meetingType: 'meet',
        modifiedAt: isoStart,
        organizer: { email: 'organizer@example.com' },
        recurringEventId: '',
        start: isoStart,
        status: 'confirmed',
        summary: `Demo sync meeting ${i + 1}`,
        meetinglogAppName: i % 2 === 0 ? 'Zoom' : 'Google Meet',
        updated: isoStart,
        meetinglogTime: 2400 + i * 100,
      });
    }
    return this.paging.from(items, options.limit ?? 200);
  }

  getDemoGoogleCalendarMeetingUsersPaged(options: any = {}): PagedResult<GoogleCalendarUsersDataItem> {
    const eventId = options.eventId || 'demo-event-0';
    const slice = this.mockStats.mockUsers.slice(0, 6);
    const eventIndex = Number((/^demo-event-(\d+)$/.exec(eventId) || [])[1] || 0);
    const base = getDemoCalendarMeetingStart(options, eventIndex);
    const items: GoogleCalendarUsersDataItem[] = slice.map((u, i) => {
      const start = base.plus({ minutes: i * 5 });
      const end = start.plus({ hours: 1 });
      const isoStart = start.toISO();
      const isoEnd = end.toISO();
      const meetinglogTime = 2000 + i * 120;
      const talkTime = 800 + i * 50;
      const idle = 200 + i * 10;
      const external = i === slice.length - 1;
      return {
        _id: `demo-stored-${eventId}-${u.id}`,
        company: 'demo',
        eventId,
        user: u.id,
        attendeeStats: this.demoGcAttendeeStats(!external),
        created: isoStart,
        createdAt: isoStart,
        creator: { email: 'organizer@example.com' },
        durationMinutes: 60,
        end: isoEnd,
        hasAttendees: true,
        idleSecMeeting: idle,
        isAllDay: false,
        isRecurring: false,
        lastSynced: isoStart,
        meetingType: 'meet',
        meetinglogAppName: 'Zoom',
        meetinglogTime,
        modifiedAt: isoStart,
        organizer: { email: 'organizer@example.com' },
        start: isoStart,
        status: 'confirmed',
        responseStatus: i % 4 === 0 ? 'needsAction' : 'accepted',
        summary: 'Demo meeting',
        syncSource: 'google',
        tags: [],
        talkTime,
        timezone: 'UTC',
        updated: isoStart,
        userName: u.name,
        userType: external ? 'external' : 'internal',
      };
    });
    return this.paging.from(items, options.limit ?? 200);
  }

  getDemoUserMeetingDetails(parameters: { userId?: string, timezone?: string }): Observable<UserMeetingDetails[]> {
    const userId = parameters.userId || this.userIds[0];
    const base = DateTime.utc(2024, 3, 10, 10, 0, 0);
    const rows: UserMeetingDetails[] = [];
    for (let i = 0; i < 5; i++) {
      const start = base.plus({ days: i });
      const end = start.plus({ hours: 1 });
      const time = 3600;
      rows.push({
        userId,
        start: start.toISO(),
        end: end.toISO(),
        time,
        idleSecMeeting: 180,
        idleSecMeetingRatio: 0.05,
        idleMinsMeeting: 3,
        idleMinsMeetingRatio: 0.05,
      });
    }
    return of(rows);
  }

  getDemoGroupSidePanelStatsRows(userFilter: string | string[]): {
    userId: string;
    userName: string;
    total: number;
    meeting: number;
    meetingRatio: number;
    meetingCount: number;
    avgMeetingDuration: number;
    avgIdleTimePerMeeting: number;
  }[] {
    const ids = Array.isArray(userFilter)
      ? userFilter
      : String(userFilter)
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
    const users = this.mockStats.mockUsers.filter(u => ids.includes(u.id));
    const list = users.length ? users : this.mockStats.mockUsers;
    return list.map((u, idx) => {
      const total = 22000 + idx * 1500;
      const meeting = Math.floor(total * (0.18 + (idx % 4) * 0.02));
      const mc = 5 + (idx % 4);
      return {
        userId: u.id,
        userName: u.name,
        total,
        meeting,
        meetingRatio: meeting / total,
        meetingCount: mc,
        avgMeetingDuration: Math.floor(meeting / mc),
        avgIdleTimePerMeeting: 100 + idx * 5,
      };
    });
  }

  getDemoMeetingSidePanelApps(): NewStatsComcatResult[] {
    return [
      { comCat: 'demo-cat-1', entity: 'app', name: 'Zoom', score: TimeuseScore.Productive, total: 2400 },
      { comCat: 'demo-cat-2', entity: 'domain', name: 'docs.google.com', score: TimeuseScore.Productive, total: 1200 },
      { comCat: 'demo-cat-3', entity: 'app', name: 'Slack', score: TimeuseScore.Neutral, total: 600 },
    ];
  }

  getDemoMeetingTimeuseForCategory(app: NewStatsComcatResult): Timeuse[] {
    const score = app.score ?? TimeuseScore.Productive;
    const entity = app.entity as TimeuseEntity;
    const base = DateTime.utc(2024, 3, 15, 14, 0, 0);
    const type = app.entity === 'domain' ? 'web' : 'app';
    return [
      {
        start: base.toISO(),
        time: 1_800_000,
        score,
        category: { id: app.comCat, entity, name: app.name, scope: 'global', score, time: 0 },
        type,
        value: 'demo-1',
        title: 'Demo session',
      },
      {
        start: base.plus({ minutes: 20 }).toISO(),
        time: 900_000,
        score,
        category: { id: app.comCat, entity, name: app.name, scope: 'global', score, time: 0 },
        type,
        value: 'demo-2',
        title: 'Demo follow-up',
      },
    ];
  }

  getDemoGoogleCalendarSummary(apiParams?: { user?: string | string[], tag?: string | string[] }): Observable<CalendarSummaryResponse> {
    const scale = this.demoMeetingFilterScale(apiParams);
    const bias = this.demoMeetingFilterBias(apiParams);
    return of({
      totalMeetingTime: Math.round(14400 * scale),
      internalMeetingsTime: Math.round(9000 * scale),
      externalMeetingsTime: Math.round(5400 * scale),
      meetingCount: Math.max(1, Math.round(12 * scale)),
      externalUserCount: Math.max(0, Math.round(4 * scale) + (bias % 2)),
      internalUserCount: Math.max(1, Math.round(8 * scale)),
      internalMeetingsCount: Math.max(1, Math.round(8 * scale)),
      externalMeetingsCount: Math.max(0, Math.round(4 * scale)),
    });
  }

  getDemoMeetingBreakdownStatsTotal(
    viewMode: 'groups' | 'users',
    apiParams?: { user?: string | string[], tag?: string | string[] },
  ): Observable<{ data: any[], paging: { totalCount: number, cur: number, limit: number, next: number | null, nItems: number } }> {
    const allUsers = this.mockStats.mockUsers || [];
    const users = this.filterMockUsersByApiParams(apiParams);
    const byTag = new Map<string, typeof users>();
    for (const u of users) {
      const list = byTag.get(u.tagId) ?? [];
      list.push(u);
      byTag.set(u.tagId, list);
    }

    let data: any[];
    if (viewMode === 'groups') {
      const tagOrder = [...new Set(allUsers.map(user => user.tagId))];
      data = Array.from(byTag.entries()).map(([tagId, list]) => {
        const idx = Math.max(tagOrder.indexOf(tagId), 0);
        const total = 25000 + idx * 4000;
        const meeting = Math.floor(total * 0.22);
        return {
          currentTagId: tagId,
          currentTagName: list[0]?.tagName ?? `Team ${idx + 1}`,
          userId: list.map(x => x.id),
          total,
          meeting,
          meetingRatio: meeting / total,
          meetingCount: 8 + idx,
          avgMeetingDuration: Math.floor(meeting / (8 + idx)),
          avgIdleTimePerMeeting: 120 + idx * 10,
        };
      });
    } else {
      data = users.map((u) => {
        const idx = Math.max(allUsers.findIndex(user => user.id === u.id), 0);
        const total = 22000 + idx * 1500;
        const meeting = Math.floor(total * (0.15 + (idx % 5) * 0.02));
        return {
          userId: u.id,
          userName: u.name,
          total,
          meeting,
          meetingRatio: meeting / total,
          meetingCount: 5 + (idx % 4),
          avgMeetingDuration: Math.floor(meeting / (5 + (idx % 4))),
          avgIdleTimePerMeeting: 100 + idx * 5,
        };
      });
    }

    return of({
      data,
      paging: {
        totalCount: data.length,
        cur: 0,
        limit: data.length,
        next: null,
        nItems: data.length,
      },
    });
  }

  private filterMockUsersByApiParams(apiParams?: { user?: string | string[], tag?: string | string[] }) {
    let users = [...(this.mockStats.mockUsers || [])];
    if (!apiParams) {
      return users;
    }

    if (apiParams.tag != null && apiParams.tag !== '') {
      const tags = Array.isArray(apiParams.tag)
        ? apiParams.tag.map(String)
        : String(apiParams.tag).split(',').map(tag => tag.trim()).filter(Boolean);
      if (tags.length) {
        users = users.filter(user => tags.includes(user.tagId));
      }
    }

    const ids = resolveMockUserIds(apiParams);
    if (ids) {
      users = users.filter(user => ids.includes(user.id));
    }

    return users;
  }

  private demoMeetingFilterScale(apiParams?: { user?: string | string[], tag?: string | string[] }): number {
    const allCount = Math.max(1, this.mockStats.mockUsers?.length || 1);
    return this.filterMockUsersByApiParams(apiParams).length / allCount;
  }

  private demoMeetingFilterBias(apiParams?: { user?: string | string[], tag?: string | string[] }): number {
    return this.filterMockUsersByApiParams(apiParams).reduce((sum, user) => sum + user.id.length, 0);
  }

  /**
   * Fires once when mock data was active in this session and tracked time turns it off.
   * Skips sessions that load with tracked time already present (mock data never shown).
   */
  private trackMockDataDismissedByTrackedTimeIfNeeded(): void {
    if (!this.mockDataWasActiveBeforeTrackedTime || this.trackedTimeDismissEventFired) {
      return;
    }
    this.trackedTimeDismissEventFired = true;
    void this.segment.trackInAppEvents('Dismissed Mock Data By Track Time');
  }

  /**
   * When real tracked time exists, persist company `custom.showDemoData: false` for owner/admin so other clients align.
   * Non-privileged roles still see mock disabled via visibility rules but cannot update company settings.
   */
  private ensureShowDemoDataSettingFalseWhenTracked(): void {
    if (this.syncingLegacyShowDemoDataOff) {
      return;
    }
    const company = this.store.selectSnapshot(AuthState.company);
    if (!company) {
      return;
    }
    if (!this.canPersistCompanyShowDemoData(company.role)) {
      return;
    }
    if (company.companySettings?.custom?.showDemoData !== true) {
      return;
    }
    this.syncingLegacyShowDemoDataOff = true;
    void this.persistShowDemoDataFalse(company)
      .catch((error: unknown) => {
        Sentry.captureException(error, {
          tags: {
            component: 'DemoService',
            action: 'persistShowDemoDataFalse',
            context: 'tracked_time_sync',
          },
        });
      })
      .finally(() => {
        this.syncingLegacyShowDemoDataOff = false;
      });
  }

  private async persistShowDemoDataFalse(company: AuthCompany): Promise<void> {
    if (!this.canPersistCompanyShowDemoData(company.role)) {
      return;
    }
    const payload = { 'custom.showDemoData': false };
    const custom: ExtCustomCompanySettings = { showDemoData: false };
    await lastValueFrom(this.companyService.putCompanySettings(
      company.id,
      payload as unknown as Parameters<CompanyService['putCompanySettings']>[1],
    ));
    this.store.dispatch(new CompanySettingsChanged({}, {}, custom));
  }

  /** Only owner or admin may set company `custom.showDemoData` to false. */
  private canPersistCompanyShowDemoData(role: string | undefined): boolean {
    return role === 'owner' || role === 'admin';
  }

  /**
   * Clears sample data for a single dashboard only (executive, team, or individual).
   * Persists on the signed-in user `userSettings.custom.demoDataHiddenDashboards` for every role.
   * Other dashboards keep sample data until cleared there or until the team tracks time.
   */
  public async dismissDemoDataForDashboard(dashboardId: DemoDashboardId): Promise<void> {
    try {
      await lastValueFrom(this.store.dispatch(new UpdateCurrentUser(true)));
    } catch (error: unknown) {
      Sentry.captureException(error, {
        tags: {
          component: 'DemoService',
          action: 'dismissDemoDataForDashboard',
          context: 'refresh_auth_before_dismiss',
        },
      });
    }

    const company = this.store.selectSnapshot(AuthState.company);

    if (!company) return;

    const custom = company.userSettings?.custom;
    const prevRaw = custom?.demoDataHiddenDashboards;
    const previousHiddenDashboards = prevRaw === undefined ? undefined : [...prevRaw];

    const existing = [...(custom?.demoDataHiddenDashboards ?? [])];
    if (existing.includes(dashboardId)) {
      return;
    }
    existing.push(dashboardId);

    const hiddenPayload = { 'custom.demoDataHiddenDashboards': existing };
    const hiddenCustom: ExtCustomCompanySettings = { demoDataHiddenDashboards: existing };

    try {
      // Optimistic merge so rapid clears / overlapping dismiss calls see the latest local list.
      this.store.dispatch(new UserSettingsChanged({}, hiddenCustom));

      await lastValueFrom(this.userService.editProperties(
        'me',
        hiddenPayload as unknown as Parameters<UserService['editProperties']>[1],
      ));
      this.growthBook.setAttribute('demo_data_banner_dismiss', true);

      this.invalidateCache();
    } catch (error) {
      this.store.dispatch(new UserSettingsChanged({}, {
        demoDataHiddenDashboards: previousHiddenDashboards,
      }));
      Sentry.captureException(error, {
        tags: {
          component: 'DemoService',
          action: 'dismissDemoDataForDashboard',
          context: 'manual_setting_demo_data',
        },
      });
      throw error;
    }
  }

  /**
   * Invalidates cached data to ensure fresh data is served
   */
  /**
   * Cleans up workLifeBalanceGroups subscription and observable
   */
  private cleanupWorkLifeBalanceGroups(): void {
    const sub = this.workLifeBalanceGroupsSubscription;
    if (sub) {
      this.activeSubscriptions.delete(sub);
      if (!sub.closed) {
        sub.unsubscribe();
      }
      this.workLifeBalanceGroupsSubscription = null;
    }
    this.workLifeBalanceGroups$ = null;
    this.lastWorkLifeBalanceParams = null;
  }

  private invalidateCache(): void {
    this.activeUsers = null;
    this.cleanupWorkLifeBalanceGroups();
  }

  /**
   * Performs periodic memory cleanup to prevent accumulation
   * Fixed: Improved to prevent potential issues with recursive calls
   */
  private performMemoryCleanup(): void {
    // Clear cached data every 5 minutes
    this.memoryCleanupTimeoutId = setTimeout(() => {
      // Only perform cleanup if service hasn't been destroyed
      if (this.destroy$ && !this.destroy$.closed) {
        this.invalidateCache();
        // Schedule next cleanup
        this.performMemoryCleanup();
      }
    }, 5 * 60 * 1000);
  }

  public getWorkLifeBalance(parameters: any, mode: 'most' | 'least' = 'most', variant: 'group' | 'list' | 'user' = 'group') {
    if (!parameters) { return of(null as unknown as WorkLifeBalanceResult[]); }
    const { tag, timezone, ...filteredParams } = parameters ?? {};
    const sort = mode === 'most' ? '_totalIssues' : 'totalIssues';

    return from(this.mockStats.workLifeBalance({
      ...(variant === 'group' ? filteredParams : parameters),
      from: DateTime.fromISO(parameters.from, { zone: parameters.timezone }).toUTC(0, { keepLocalTime: true }).toISO(),
      to: DateTime.fromISO(parameters.to, { zone: parameters.timezone }).toUTC(0, { keepLocalTime: true }).toISO(),
      fields: 'totalHours,lastHour,day,tooManyHours,lateHours,outOfShiftWork,weekendWork,dailyIssues,totalIssues',
      sort,
      period: 'days',
      interval: 1,
      paging: 1,
      'user-tz': 1,
      'post-aggr': 'date',
      resolve: 'userId',
      'group-by': 'date,userId',
      limit: 100,
    }).page(0)).pipe(
      startWith(null as unknown as WorkLifeBalanceResult[]),
      this.mockStats.withErrorHandling([] as WorkLifeBalanceResult[]),
      takeUntil(this.destroy$),
    );
  }

  public getWorkLifeBalanceGroups(parameters: any) {
    if (!parameters) {
      // Clean up existing subscription and observable
      this.cleanupWorkLifeBalanceGroups();
      return of(null as unknown as WorkLifeBalanceResult[]);
    }

    // Check if parameters have changed to determine if we should invalidate cache
    const paramsChanged = this.lastWorkLifeBalanceParams === null ||
                         JSON.stringify(this.lastWorkLifeBalanceParams) !== JSON.stringify(parameters);

    if (paramsChanged) {
      // Clean up existing subscription and observable when parameters change
      this.cleanupWorkLifeBalanceGroups();
      this.lastWorkLifeBalanceParams = parameters;
    } else if (this.workLifeBalanceGroups$) {
      // Return cached observable if parameters haven't changed
      return this.workLifeBalanceGroups$;
    }

    const { tag, timezone, ...filteredParams } = parameters ?? {};
    const result = from(this.mockStats.workLifeBalance({
      ...filteredParams,
      user: 'all-on-reports',
      from: DateTime.fromISO(parameters.from, { zone: parameters.timezone }).toUTC(0, { keepLocalTime: true }).toISO(),
      to: DateTime.fromISO(parameters.to, { zone: parameters.timezone }).toUTC(0, { keepLocalTime: true }).toISO(),
      fields: 'currentTagId,dailyIssues,totalIssues',
      sort: '_average',
      period: 'days',
      interval: 1,
      paging: 1,
      resolve: 'currentTagId',
      limit: 20,
      'user-tz': 1,
      'post-aggr': 'userId,date',
      'group-by': 'currentTagId,userId,date',
      'result[average]': 'totalIssues/@userId',
      'bottom-limit': 20,
      'api-cache': 24 * 60 * 60 * 2,
      'api-cache-ignore': 'from,to,timezone',
    }, false).page(0))
      .pipe(
        startWith(null as unknown as WorkLifeBalanceResult[]),
        this.mockStats.withErrorHandling([] as WorkLifeBalanceResult[]),
        takeUntil(this.destroy$),
        finalize(() => {
          // Clean up subscription when observable completes
          this.cleanupWorkLifeBalanceGroups();
        }),
      );

    this.workLifeBalanceGroups$ = result;

    // Subscribe to track the actual subscription for proper cleanup
    this.workLifeBalanceGroupsSubscription = result.subscribe();
    this.activeSubscriptions.add(this.workLifeBalanceGroupsSubscription);

    return result;
  }

  public getNewStats(parameters: any, minTime: number, variant: string, mode: HighestRatioMode): Observable<any> {
    const { tag, ...filteredParams } = parameters ?? {};
    const productive = mode !== 'unproductive';
    const groupParams: Partial<NewStatsOptions & { 'group-by': 'userId' | 'currentTagId' }> = variant === 'group'
      ? {
        fields: ['currentTagId', 'ratio', 'total'].join(','),
        'group-by': 'currentTagId',
        resolve: 'currentTagId',
        user: 'all-on-reports',
        'api-cache': 24 * 60 * 60 * 2,
        'api-cache-ignore': 'from,to,timezone',
      }
      : {};

    return from(this.mockStats.newStats({
      ...(variant === 'group' ? filteredParams : parameters),
      limit: 20,
      sort: '_ratio',
      'filter[total]': `${minTime ?? 60}_`,
      'group-by': 'userId',
      resolve: 'userId',
      'ratio-id': productive ? TimeuseScore.Productive : TimeuseScore.Unproductive,
      'ratio-limit': -1,
      ratio: 'score',
      ...groupParams,
    }, mode).page(0)).pipe(
      takeUntil(this.destroy$),
    );
  }

  /**
   * Generates mock data for newStatsTimesheet method
   * Mimics the behavior of StatsService.newStatsTimesheet with demo data
   */
  public generateNewStatsTimesheet(options: NewStatsOptions & { 'group-by'?: 'company' }): Observable<NewStatsCompanyResult[][]>;
  public generateNewStatsTimesheet(options: NewStatsOptions & { 'group-by': 'userId' }): Observable<NewStatsUserResult[][]>;
  public generateNewStatsTimesheet(options: NewStatsOptions): Observable<NewStatsCompanyResult[][] | NewStatsUserResult[][]> {
    const buckets = getDemoDateBuckets(options);
    const users = filterDemoReportUsers(buildDemoReportUsers(), {
      ...(options.user != null ? { user: options.user } : {}),
      ...(options.tag != null ? { tag: options.tag } : {}),
    });

    if (!users.length) {
      return of(buckets.map(() => [{
        total: 0,
        ratio: 0,
        ratioCount: 0,
        count: 0,
        score: [],
      }]));
    }

    if (options['group-by'] === 'userId') {
      return this.generateUserTimesheetData(buckets, options, users);
    }

    return this.generateCompanyTimesheetData(buckets, options, users.length);
  }

  private generateCompanyTimesheetData(
    buckets: ReturnType<typeof getDemoDateBuckets>,
    options: NewStatsOptions,
    userCount = this.userCount,
  ): Observable<NewStatsCompanyResult[][]> {
    const standardRatios = this.mockStats.standardRatios;

    const data = buckets.map((bucket, dayIdx) => {
      const bucketOptions = withBucketDateOptions(options, bucket);
      const bucketTotals = this.mockStats.getDemoTotals(bucketOptions);
      const totalSeconds = Math.round((bucketTotals.total ?? 0) * this.randomBetween(0.9, 1, 'company-timesheet', bucket.start.toISODate(), dayIdx));

      const prodRatio = standardRatios.productive * this.randomBetween(0.8, 0.9, 'company-timesheet', bucket.start.toISODate(), dayIdx, 'prod');
      const unprodRatio = standardRatios.unproductive * this.randomBetween(0.8, 0.9, 'company-timesheet', bucket.start.toISODate(), dayIdx, 'unprod');
      const neutralRatio = standardRatios.neutral * this.randomBetween(0.8, 0.9, 'company-timesheet', bucket.start.toISODate(), dayIdx, 'neutral');
      const idleRatio = standardRatios.idle * this.randomBetween(0.8, 0.9, 'company-timesheet', bucket.start.toISODate(), dayIdx, 'idle');
      const prodTotal = Math.round(totalSeconds * prodRatio);
      const unprodTotal = Math.round(totalSeconds * unprodRatio);
      const neutralTotal = Math.round(totalSeconds * neutralRatio);
      const idleTotal = totalSeconds - (prodTotal + unprodTotal);

      return [{
        total: totalSeconds,
        ratio: prodRatio,
        ratioCount: userCount * Math.floor(this.randomBetween(8, 12, 'company-timesheet', bucket.start.toISODate(), dayIdx, 'ratioCount')),
        count: userCount,
        score: [
          { id: '4', total: prodTotal, ratio: prodRatio },
          { id: '1', total: neutralTotal, ratio: neutralRatio },
          { id: '2', total: unprodTotal, ratio: unprodRatio },
          { id: '3', total: idleTotal, ratio: idleRatio },
        ],
      }];
    });

    return of(data);
  }

  private generateUserTimesheetData(
    buckets: ReturnType<typeof getDemoDateBuckets>,
    options: NewStatsOptions,
    users: User[] = buildDemoReportUsers(),
  ): Observable<NewStatsUserResult[][]> {
    const standardRatios = this.mockStats.standardRatios;
    const userIds = users.map(user => user.id);
    const userNames = users.map(user => user.name);

    const data = buckets.map((bucket, dayIdx) => {
      const bucketOptions = withBucketDateOptions(options, bucket);
      const bucketTotals = this.mockStats.getDemoTotals(bucketOptions);
      const dayTotalSeconds = Math.round((bucketTotals.total ?? 0) * this.randomBetween(0.9, 1.1, 'user-timesheet', bucket.start.toISODate(), dayIdx));
      const userTotals = this.distributeTotal(dayTotalSeconds, userIds.length);

      return userIds.map((userId, index) => {
        const totalSeconds = userTotals[index];
        const userVariation = this.randomBetween(0.9, 1.1, 'user-timesheet', bucket.start.toISODate(), dayIdx, 'user', userId, 'variation');
        const prodRatio = standardRatios.productive * userVariation;
        const unprodRatio = standardRatios.unproductive * userVariation;
        const neutralRatio = standardRatios.neutral * userVariation;
        const prodTotal = Math.round(totalSeconds * prodRatio);
        const unprodTotal = Math.round(totalSeconds * unprodRatio);
        const neutralTotal = Math.round(totalSeconds * neutralRatio);
        const tagRoll = this.demoSeed.float('user-timesheet', bucket.start.toISODate(), dayIdx, 'user', userId, 'tag');
        const tagIndex = this.demoSeed.int(1, 3, 'user-timesheet', bucket.start.toISODate(), dayIdx, 'user', userId, 'tagIndex');

        return {
          userId,
          userName: userNames[index],
          userArchived: false,
          total: totalSeconds,
          ratio: prodRatio,
          ratioCount: Math.floor(this.randomBetween(8, 12, 'user-timesheet', bucket.start.toISODate(), dayIdx, 'user', userId, 'ratioCount')),
          currentTagId: tagRoll > 0.3 ? `tag-${tagIndex}` : undefined,
          currentTagName: tagRoll > 0.3 ? `Team ${String.fromCharCode(64 + tagIndex)}` : undefined,
          score: [
            { id: '4', total: prodTotal, ratio: prodTotal / totalSeconds },
            { id: '1', total: unprodTotal, ratio: unprodTotal / totalSeconds },
            { id: '2', total: neutralTotal, ratio: neutralTotal / totalSeconds },
          ],
        };
      });
    });

    return of(data);
  }

  // Helper function to distribute a total among n items with some variation
  private distributeTotal(total: number, n: number): number[] {
    const randomValues = Array(n).fill(0).map((_, i) =>
      this.randomBetween(0.8, 1.2, 'distribute-total', i),
    );

    const sum = randomValues.reduce((acc, val) => acc + val, 0);

    return randomValues.map(val => Math.round(total * (val / sum)));
  }

  private randomBetween(min: number, max: number, ...scope: (string | number)[]): number {
    return this.demoSeed.between(min, max, 'demo-service', ...scope);
  }

  /**
   * Resolves user IDs to user objects using mockUsers data
   * @param userIds Single user ID or array of user IDs to resolve
   * @returns Promise that resolves to a Map of user IDs to user object mappings
   */
  public resolveUsers(userIds: string | string[]): Promise<Map<string, any>> {
    // Create a Map object to store user ID to user object mappings
    const userMap = new Map<string, any>();

    // Convert single user ID to array if needed
    const ids = Array.isArray(userIds) ? userIds : [userIds];

    // Find matching users from mockUsers and add them to the map
    ids.forEach(userId => {
      const user = this.mockStats.mockUsers.find(u => u.id === userId);
      if (user) {
        userMap.set(userId, user);
      }
    });

    // Always return a Promise that resolves with the userMap
    return Promise.resolve(userMap);
  }

  /**
   * Maps internal mock-store users to report {@link User} models for dashboard pickers.
   * Silent companies receive `trackingMode: 'silent'` on every demo employee.
   */
  public getDemoReportUsers(companyTrackingMode?: 'interactive' | 'silent' | 'mixed' | 'automatic'): User[] {
    const isSilentCompany = companyTrackingMode === 'silent' || companyTrackingMode === 'automatic';
    return this.mockStats.mockUsers.map(mockUser => this.mapMockStoreUserToReportUser(mockUser, isSilentCompany));
  }

  /** Resolves a catalog demo user id. Returns null for unknown / live ids — never remaps onto the default mock user. */
  public resolveDemoUser(
    userId: string,
    companyTrackingMode?: 'interactive' | 'silent' | 'mixed' | 'automatic',
  ): User | null {
    if (!userId) {
      return null;
    }
    const users = this.getDemoReportUsers(companyTrackingMode);
    return users.find(u => u.id === userId) ?? null;
  }

  /** Default demo employee for the individual dashboard user picker. */
  public getDefaultDemoReportUser(
    companyTrackingMode?: 'interactive' | 'silent' | 'mixed' | 'automatic',
  ): User | null {
    const users = this.getDemoReportUsers(companyTrackingMode);
    if (!users.length) {
      return null;
    }
    const isSilentCompany = companyTrackingMode === 'silent' || companyTrackingMode === 'automatic';
    if (isSilentCompany) {
      return users.find(u => u.id === DEMO_SILENT_USER_ID) ?? users[0];
    }
    return users[0];
  }

  private mapMockStoreUserToReportUser(mockUser: MockStoreUser, isSilentCompany: boolean): User {
    return {
      id: mockUser.id,
      name: mockUser.name,
      email: mockUser.email,
      archived: mockUser.archived,
      active: true,
      showOnReports: true,
      role: 'user',
      trackingMode: isSilentCompany ? 'silent' : 'interactive',
      lastTrack: buildDemoReportLastTrack(mockUser.id),
    } as User;
  }
}
